import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Row, Col, Modal, Form, Input, InputNumber, Select, Space, Tag, message, Tabs, Popconfirm, DatePicker, AutoComplete } from 'antd';
import { PlusOutlined, ReloadOutlined, ImportOutlined, DeleteOutlined, EditOutlined, FileTextOutlined, AccountBookOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiFetch, onDataChanged } from '../utils/api.js';
import { yuan } from '../utils/format.js';
import { prodCode } from '../utils/prodCode.js';
import { useAuth } from '../auth.jsx';

export default function Purchase() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [operators, setOperators] = useState([]); // 历史入库操作人
  const [poModal, setPoModal] = useState(null);
  const [inboundModal, setInboundModal] = useState(null);
  const [adjustModal, setAdjustModal] = useState(null); // {po_id, item_idx, spec}
  const [adjRows, setAdjRows] = useState([]); // [{inbound_idx, qty, operator, date}]
  const [supModal, setSupModal] = useState(false);
  const [whModal, setWhModal] = useState(false);
  const [form] = Form.useForm();
  const [supForm] = Form.useForm();
  const [whForm] = Form.useForm();
  const [inboundForm] = Form.useForm();
  const [ciForm] = Form.useForm();
  const [rows, setRows] = useState([{ spec_code: '', product_name: '', quantity: undefined }]);
  // 进项成本票
  const [costInvoices, setCostInvoices] = useState([]);
  const [ciSummary, setCiSummary] = useState(null);
  const [ciMeta, setCiMeta] = useState({ types: [], statuses: [], suppliers: [], entities: [] });
  const [ciModal, setCiModal] = useState(null); // null | {}新增 | {record}编辑
  const [supEdit, setSupEdit] = useState(null); // null | {record}编辑供应商
  // 应付账期
  const [payables, setPayables] = useState([]);
  const [payableSum, setPayableSum] = useState(null);
  const [payableMeta, setPayableMeta] = useState({ suppliers: [], purchase_orders: [] });
  const [payableModal, setPayableModal] = useState(null); // null | {}新增 | {record}编辑

  const loadData = useCallback(() => {
    apiFetch('/api/purchase-orders').then(r => r.json()).then(d => setPos(d.data || [])).catch(() => {});
    apiFetch('/api/suppliers').then(r => r.json()).then(d => setSuppliers(d.data || [])).catch(() => {});
    apiFetch('/api/warehouses').then(r => r.json()).then(d => setWarehouses(d.data || [])).catch(() => {});
    apiFetch('/api/products').then(r => r.json()).then(d => setProducts(d.data || [])).catch(() => {});
    apiFetch('/api/inbound-operators').then(r => r.json()).then(d => setOperators(d.data || [])).catch(() => {});
    apiFetch('/api/cost-invoice').then(r => r.json()).then(d => setCostInvoices(d.data || [])).catch(() => {});
    apiFetch('/api/cost-invoice/summary').then(r => r.json()).then(d => setCiSummary(d.data || null)).catch(() => {});
    apiFetch('/api/cost-invoice/meta').then(r => r.json()).then(d => setCiMeta(d || { types: [], statuses: [], suppliers: [], entities: [] })).catch(() => {});
    apiFetch('/api/payable').then(r => r.json()).then(d => { setPayables(d.data || []); setPayableSum(d.summary || null); setPayableMeta({ suppliers: d.suppliers || [], purchase_orders: d.purchase_orders || [] }); }).catch(() => {});
  }, []);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => onDataChanged(() => { loadData(); }), [loadData]);

  // 商品选项：按产品编码（上级）去重，入库/采购只选上级编码
  const prodOptMap = {};
  products.forEach(p => { const k = prodCode(p.spec_code); if (!prodOptMap[k]) prodOptMap[k] = { name: p.product_name || k }; });
  const productOptions = Object.entries(prodOptMap).map(([code, v]) => ({ value: code, label: `${code}（${v.name}）` }));

  // 采购计划表单行
  const updateRow = (i, k, v) => {
    const nr = rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r);
    if (k === 'spec_code') {
      const p = products.find(x => prodCode(x.spec_code) === v) || products.find(x => x.spec_code === v);
      nr[i].product_name = p ? p.product_name : '';
    }
    setRows(nr);
  };
  const addRow = () => setRows([...rows, { spec_code: '', product_name: '', quantity: undefined }]);
  const delRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const submitPo = async () => {
    const v = await form.validateFields();
    const items = rows.filter(r => r.spec_code && r.quantity > 0);
    if (!items.length) return message.error('至少添加一个采购商品');
    const res = await apiFetch('/api/purchase-orders', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        purchase_date: v.purchase_date ? v.purchase_date.format('YYYY-MM-DD') : '',
        purchase_month: v.purchase_month ? v.purchase_month.format('YYYY-MM') : '',
        registrar: v.registrar || '', supplier_id: v.supplier_id, warehouse_id: v.warehouse_id, items,
      }),
    }).then(r => r.json());
    if (res.success) { message.success('采购计划已创建'); setPoModal(null); form.resetFields(); setRows([{ spec_code: '', product_name: '', quantity: undefined }]); loadData(); }
    else message.error(res.error || '创建失败');
  };

  const submitInbound = async () => {
    const v = await inboundForm.validateFields();
    const res = await apiFetch('/api/purchase-inbound', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ po_id: inboundModal.po_id, item_idx: inboundModal.item_idx, quantity: v.quantity, operator: v.operator, date: v.date ? v.date.format('YYYY-MM-DD') : '' }),
    }).then(r => r.json());
    if (res.success) { message.success('入库成功，库存已增加'); setInboundModal(null); inboundForm.resetFields(); loadData(); }
    else message.error(res.error || '入库失败');
  };

  // 调整入库：保存修改后的入库记录（数量/操作人/时间）
  const submitAdjust = async () => {
    const updates = adjRows.map(r => ({ inbound_idx: r.inbound_idx, quantity: r.qty, operator: r.operator, date: r.date }));
    if (!updates.length) return message.error('没有入库记录');
    const res = await apiFetch('/api/purchase-inbound/adjust', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ po_id: adjustModal.po_id, item_idx: adjustModal.item_idx, updates }),
    }).then(r => r.json());
    if (res.success) { message.success('已调整，库存已同步'); setAdjustModal(null); loadData(); }
    else message.error(res.error || '调整失败');
  };

  const submitSup = async () => {
    const v = await supForm.validateFields();
    const url = supEdit ? ('/api/suppliers/' + supEdit.id) : '/api/suppliers';
    const res = await apiFetch(url, { method: supEdit ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: v.name, tax_no: v.tax_no || '' }) }).then(r => r.json());
    if (res.success) { message.success(supEdit ? '已保存供应商' : '已添加供应商'); setSupModal(false); setSupEdit(null); supForm.resetFields(); loadData(); }
    else message.error(res.error || '保存失败');
  };
  const submitWh = async () => {
    const v = await whForm.validateFields();
    const res = await apiFetch('/api/warehouses', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: v.name }) }).then(r => r.json());
    if (res.success) { message.success('已添加仓库'); setWhModal(false); whForm.resetFields(); loadData(); }
  };

  // 进项成本票：新增/编辑提交
  const submitCi = async () => {
    const v = await ciForm.validateFields();
    const isEdit = ciModal && ciModal.record;
    const url = isEdit ? ('/api/cost-invoice/' + ciModal.record.id) : '/api/cost-invoice';
    const body = {
      supplier_id: v.supplier_id, invoice_no: v.invoice_no, invoice_code: v.invoice_code || '',
      invoice_type: v.invoice_type, amount_total: v.amount_total, tax: v.tax || 0,
      entity_id: v.entity_id, invoice_date: v.invoice_date ? v.invoice_date.format('YYYY-MM-DD') : '',
      status: v.status, remark: v.remark || '',
    };
    const res = await apiFetch(url, { method: isEdit ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json());
    if (res.success) { message.success(isEdit ? '已保存成本票' : '已登记成本票'); setCiModal(null); ciForm.resetFields(); loadData(); }
    else message.error(res.error || '保存失败');
  };

  // 应付账期：新增/编辑提交
  const submitPayable = async () => {
    if (!payableModal) return;
    const v = payableModal.form;
    if (!v.supplier_id) return message.warning('请选择供应商');
    if (!v.amount || Number(v.amount) <= 0) return message.warning('请填写正数的金额');
    const isEdit = payableModal.record;
    const url = isEdit ? ('/api/payable/' + payableModal.record.id) : '/api/payable';
    const body = {
      supplier_id: v.supplier_id, amount: Number(v.amount), due_date: v.due_date || '',
      status: v.status || '未付', po_id: v.po_id || null, pay_date: v.pay_date || '', remark: v.remark || '',
    };
    const res = await apiFetch(url, { method: isEdit ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json());
    if (res.success) { message.success(isEdit ? '已保存应付账款' : '已登记应付账款'); setPayableModal(null); loadData(); }
    else message.error(res.error || '保存失败');
  };
  const delPayable = async (id) => {
    const res = await apiFetch('/api/payable/' + id, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除'); loadData(); }
    else message.error(res.error || '删除失败');
  };

  // 每个商品 = 一行；采购单信息（时间/月/登记人/供应商/仓库）用 Card 头展示，每商品行重复 head 信息简洁
  const itemColumns = [
    {
      title: '采购商品', key: 'product', width: 180,
      render: (_, row) => (<div><div><b>{row.spec_code}</b></div><div style={{ color: '#8c8c8c', fontSize: 12 }}>{row.product_name}</div></div>),
    },
    { title: '采购数量', dataIndex: 'quantity', width: 90, align: 'right' },
    { title: '已入库', key: 'inbound_qty', width: 90, align: 'right',
      render: (_, row) => <span style={{ color: 'var(--brand)', fontWeight: 600 }}>{row.inbound_qty || 0}</span> },
    { title: '剩余数量', key: 'remaining', width: 90, align: 'right',
      render: (_, row) => <b style={{ color: row.remaining > 0 ? '#CF1322' : '#3F8600' }}>{row.remaining}</b> },
    { title: '入库数量', key: 'last_qty', width: 90, align: 'right',
      render: (_, row) => { const last = (row.inbound || []).slice(-1)[0]; return last ? <b>{last.qty}</b> : <span style={{ color: '#bfbfbf' }}>—</span>; } },
    { title: '入库操作人', key: 'last_operator', width: 100,
      render: (_, row) => { const last = (row.inbound || []).slice(-1)[0]; return last?.operator ? <Tag color="#7F77DD">{last.operator}</Tag> : <span style={{ color: '#bfbfbf' }}>—</span>; } },
    { title: '入库时间', key: 'last_date', width: 110,
      render: (_, row) => { const last = (row.inbound || []).slice(-1)[0]; return last?.date || <span style={{ color: '#bfbfbf' }}>—</span>; } },
    { title: '入库明细', key: 'all_inbound', width: 260,
      render: (_, row) => (
        <div style={{ fontSize: 12, color: '#595959' }}>
          {(row.inbound || []).length
            ? row.inbound.map((b, bi) => (
              <div key={bi}>
                <b style={{ color: 'var(--brand-strong)' }}>{b.qty}</b> · {b.operator || '—'} · {b.date}
              </div>
            ))
            : <span style={{ color: '#bfbfbf' }}>未入库</span>}
        </div>
      ),
    },
    {
      title: '操作', key: 'action', width: 200, align: 'center', fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" type="primary" ghost icon={<ImportOutlined />} disabled={row.status === '完成'}
            onClick={() => {
              inboundForm.resetFields();
              const last = (row.inbound || []).slice(-1)[0];
              if (last) inboundForm.setFieldsValue({ operator: last.operator });
              setInboundModal({ po_id: row.po_id, item_idx: row.idx, spec: row.spec_code, qty: row.quantity, done: row.inbound_qty || 0 });
            }}>入库</Button>
          <Button size="small" disabled={!(row.inbound || []).length}
            onClick={() => {
              setAdjRows((row.inbound || []).map((b, bi) => ({ inbound_idx: bi, qty: b.qty, operator: b.operator || '', date: b.date })));
              setAdjustModal({ po_id: row.po_id, item_idx: row.idx, spec: row.spec_code });
            }}>调整</Button>
          {row.status === '完成' && <Tag color="#3F8600">完成</Tag>}
          {row.status !== '完成' && row.remaining > 0 && <Tag color="#EF9F27">剩{row.remaining}</Tag>}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <Card
        title="采购管理（采购计划 → 分批入库，累计入库 ≥ 采购数量即完成，剩余自动计算；入库自动增加库存）"
        extra={<Space wrap><Button icon={<PlusOutlined />} type="primary" onClick={() => { form.resetFields(); setRows([{ spec_code: '', product_name: '', quantity: undefined }]); setPoModal({}); }}>新建采购计划</Button><Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button></Space>}
      >
        <Tabs
          items={[
            {
              key: 'po', label: `采购计划（${pos.length}）`,
              children: (
                <div>
                  {pos.length === 0 && <div style={{ padding: 24, color: '#8c8c8c', textAlign: 'center' }}>暂无采购计划</div>}
                  {pos.map(po => {
                    const pm = String(po.purchase_month || '').split('-');
                    const monthStr = pm.length === 2 ? `${pm[0]}年${Number(pm[1])}月` : (po.purchase_month || '—');
                    return (
                      <Card key={po.id} size="small" style={{ marginBottom: 12 }}
                        title={
                          <Space size="middle" wrap>
                            <span><b>采购时间</b>：{po.purchase_date || '—'}</span>
                            <span><b>采购月</b>：{monthStr}</span>
                            <span><b>登记人</b>：{po.registrar || '—'}</span>
                            <span><b>供应商</b>：{po.supplier_name || '—'}</span>
                            <span><b>仓库</b>：{po.warehouse_name || '—'}</span>
                            <Tag color={po.status === '已完成' ? 'green' : 'orange'}>{po.status}</Tag>
                          </Space>
                        }
                        extra={
                          <Popconfirm title="删除该采购计划（全部入库记录）？" onConfirm={async () => { await apiFetch('/api/purchase-orders/' + po.id, { method: 'DELETE' }).then(r => r.json()); loadData(); message.success('已删除'); }}>
                            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
                          </Popconfirm>
                        }
                      >
                        <Table rowKey={(row) => `${po.id}-${row.idx}`} size="small" pagination={false}
                          columns={itemColumns}
                          dataSource={(po.items || []).map((it, idx) => ({ ...it, po_id: po.id, idx }))}
                          scroll={{ x: 1200 }} />
                      </Card>
                    );
                  })}
                </div>
              ),
            },
            {
              key: 'sup', label: `供应商（${suppliers.length}）`,
              children: (
                <div>
                  <Button icon={<PlusOutlined />} type="primary" style={{ marginBottom: 12 }} onClick={() => { supForm.resetFields(); setSupEdit(null); setSupModal(true); }}>新增供应商</Button>
                  <Table rowKey="id" size="small" pagination={false} dataSource={suppliers}
                    columns={[
                      { title: '编号', dataIndex: 'id', width: 70 },
                      { title: '供应商名称', dataIndex: 'name' },
                      { title: '税号', dataIndex: 'tax_no', width: 220, render: (v) => v || <span style={{ color: '#bfbfbf' }}>未填</span> },
                      { title: '操作', width: 140, render: (_, s) => (
                        <Space size={4}>
                          <Button size="small" icon={<EditOutlined />} onClick={() => { supForm.setFieldsValue({ name: s.name, tax_no: s.tax_no || '' }); setSupEdit(s); setSupModal(true); }}>编辑</Button>
                          <Popconfirm title="删除该供应商？" onConfirm={async () => { await apiFetch('/api/suppliers/' + s.id, { method: 'DELETE' }).then(r => r.json()); loadData(); }}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                        </Space>
                      ) },
                    ]} />
                </div>
              ),
            },
            {
              key: 'wh', label: `仓库（${warehouses.length}）`,
              children: (
                <div>
                  <Button icon={<PlusOutlined />} type="primary" style={{ marginBottom: 12 }} onClick={() => { whForm.resetFields(); setWhModal(true); }}>新增仓库</Button>
                  <Table rowKey="id" size="small" pagination={false} dataSource={warehouses}
                    columns={[{ title: '编号', dataIndex: 'id', width: 80 }, { title: '仓库名称', dataIndex: 'name' },
                      { title: '操作', width: 80, render: (_, w) => <Popconfirm title="删除该仓库？" onConfirm={async () => { await apiFetch('/api/warehouses/' + w.id, { method: 'DELETE' }).then(r => r.json()); loadData(); }}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm> }]} />
                </div>
              ),
            },
            {
              key: 'ci', label: `成本票（${costInvoices.length}）`,
              children: (
                <div>
                  {ciSummary && (
                    <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                      <Col xs={24} sm={12} md={6}>
                        <Card size="small" style={{ borderRadius: 12, borderLeft: '3px solid var(--brand)' }}>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>累计票额（价税合计）</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#26201C' }}>¥{yuan(ciSummary.total_amount)}</div>
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card size="small" style={{ borderRadius: 12, borderLeft: '3px solid #EF9F27' }}>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>专票可抵扣进项</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#26201C' }}>¥{yuan(ciSummary.special_tax)}</div>
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card size="small" style={{ borderRadius: 12, borderLeft: '3px solid #1F5C99' }}>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>票数</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#26201C' }}>{ciSummary.count} 张</div>
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card size="small" style={{ borderRadius: 12, borderLeft: '3px solid ' + (ciSummary.missing_invoice_suppliers.length ? '#CF1322' : '#3F8600') }}>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>缺票供应商</div>
                          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5, color: ciSummary.missing_invoice_suppliers.length ? '#CF1322' : '#3F8600' }}>
                            {ciSummary.missing_invoice_suppliers.length ? ciSummary.missing_invoice_suppliers.join('、') : '无缺票'}
                          </div>
                        </Card>
                      </Col>
                    </Row>
                  )}
                  <Button icon={<PlusOutlined />} type="primary" style={{ marginBottom: 12 }} onClick={() => { ciForm.resetFields(); setCiModal({}); }}>登记成本票</Button>
                  <Table rowKey="id" size="small" pagination={{ pageSize: 20, showSizeChanger: false }} dataSource={costInvoices}
                    scroll={{ x: 1100 }}
                    columns={[
                      { title: '供应商', key: 'supplier', width: 180, render: (_, v) => (<div><div><b>{v.supplier_name}</b></div>{v.supplier_tax_no && <div style={{ fontSize: 12, color: '#8c8c8c' }}>{v.supplier_tax_no}</div>}</div>) },
                      { title: '发票号', dataIndex: 'invoice_no', width: 150, render: (v) => v || <span style={{ color: '#bfbfbf' }}>—</span> },
                      { title: '类型', dataIndex: 'invoice_type', width: 80, render: (v) => v === '专票' ? <Tag color="red">专票</Tag> : <Tag color="blue">普票</Tag> },
                      { title: '价税合计', dataIndex: 'amount_total', width: 110, align: 'right', render: (v) => '¥' + yuan(v) },
                      { title: '税额', dataIndex: 'tax', width: 100, align: 'right', render: (v) => <span style={{ color: v > 0 ? '#D85A30' : '#8c8c8c' }}>¥{yuan(v)}</span> },
                      { title: '主体', dataIndex: 'entity_name', width: 130, render: (v) => v || <span style={{ color: '#bfbfbf' }}>未挂</span> },
                      { title: '状态', dataIndex: 'status', width: 90, render: (v) => <Tag color={v === '已抵扣' ? 'purple' : v === '待开具' ? 'orange' : 'green'}>{v}</Tag> },
                      { title: '开票日期', dataIndex: 'invoice_date', width: 110, render: (v) => v || <span style={{ color: '#bfbfbf' }}>—</span> },
                      { title: '操作', key: 'action', width: 130, fixed: 'right', render: (_, v) => (
                        <Space size={4}>
                          <Button size="small" icon={<EditOutlined />} onClick={() => { ciForm.setFieldsValue({ supplier_id: v.supplier_id, invoice_no: v.invoice_no, invoice_type: v.invoice_type, amount_total: v.amount_total, tax: v.tax, entity_id: v.entity_id, invoice_date: v.invoice_date ? dayjs(v.invoice_date) : null, status: v.status, remark: v.remark }); setCiModal({ record: v }); }}>编辑</Button>
                          <Popconfirm title="删除该成本票？" onConfirm={async () => { await apiFetch('/api/cost-invoice/' + v.id, { method: 'DELETE' }).then(r => r.json()); loadData(); }}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                        </Space>
                      ) },
                    ]}
                    locale={{ emptyText: '暂无成本票。点上方「登记成本票」，把供应商开来的进货发票记进来。' }}
                  />
                </div>
              ),
            },
            ...(isAdmin ? [{
              key: 'payable', label: <span><AccountBookOutlined /> 应付账期</span>,
              children: (
                <div>
                  {payableSum && (
                    <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                      <Col xs={24} sm={8} md={8}>
                        <Card size="small" style={{ borderRadius: 12, borderLeft: '3px solid #1F5C99' }}>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>未付总额</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#26201C' }}>¥{yuan(payableSum.unpaid_amount)}</div>
                        </Card>
                      </Col>
                      <Col xs={24} sm={8} md={8}>
                        <Card size="small" style={{ borderRadius: 12, borderLeft: '3px solid ' + (payableSum.overdue_count ? '#CF1322' : '#3F8600') }}>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>逾期未付</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: payableSum.overdue_count ? '#CF1322' : '#3F8600' }}>¥{yuan(payableSum.overdue_amount)}</div>
                        </Card>
                      </Col>
                      <Col xs={24} sm={8} md={8}>
                        <Card size="small" style={{ borderRadius: 12, borderLeft: '3px solid var(--brand)' }}>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>未付 / 逾期笔数</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#26201C' }}>{payableSum.unpaid_count} / {payableSum.overdue_count}</div>
                        </Card>
                      </Col>
                    </Row>
                  )}
                  <Button icon={<PlusOutlined />} type="primary" style={{ marginBottom: 12 }} onClick={() => setPayableModal({ form: { supplier_id: undefined, amount: '', due_date: '', status: '未付', po_id: undefined, pay_date: '', remark: '' }, record: null })}>登记应付账款</Button>
                  <Table rowKey="id" size="small" pagination={{ pageSize: 20, showSizeChanger: false }} dataSource={payables}
                    scroll={{ x: 1320 }}
                    columns={[
                      { title: '供应商', dataIndex: 'supplier_name', width: 170, render: (v, r) => <span>{r.supplier_code ? <span style={{ color: '#A08C82', marginRight: 6, fontSize: 12 }}>{r.supplier_code}</span> : null}<b>{v}</b></span> },
                      { title: '编号', dataIndex: 'ap_no', width: 130, render: v => v ? <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>{v}</span> : <span style={{ color: '#bfbfbf' }}>—</span> },
                      { title: '总额', dataIndex: 'amount', width: 100, align: 'right', render: v => <b style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>¥{yuan(v)}</b> },
                      { title: '已付', dataIndex: 'paid_amount', width: 90, align: 'right', render: (v) => Number(v) > 0 ? <span style={{ color: '#3F8600', fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>¥{yuan(v)}</span> : <span style={{ color: '#bfbfbf' }}>—</span> },
                      { title: '剩余未付', dataIndex: 'remaining', width: 100, align: 'right', render: (v, r) => r.status === '已付' ? <span style={{ color: '#3F8600' }}>已结清</span> : <b style={{ color: '#CF1322', fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>¥{yuan(v)}</b> },
                      { title: '到期日', dataIndex: 'due_date', width: 100, render: v => v || <span style={{ color: '#bfbfbf' }}>—</span> },
                      { title: '状态', dataIndex: 'status', width: 78, render: v => v === '已付' ? <Tag color="green">已付</Tag> : <Tag color="orange">未付</Tag> },
                      { title: '逾期天数', dataIndex: 'overdue_days', width: 95, align: 'right', render: (v, r) => r.overdue ? <Tag color="red" style={{ borderRadius: 999 }}>{v} 天</Tag> : <span style={{ color: '#bfbfbf' }}>—</span> },
                      { title: '付款日期', dataIndex: 'pay_date', width: 100, render: v => v || <span style={{ color: '#bfbfbf' }}>—</span> },
                      { title: '备注', dataIndex: 'remark', width: 150, render: v => v || '—' },
                      { title: '操作', key: 'action', width: 130, fixed: 'right', render: (_, v) => (
                        <Space size={4}>
                          <Button size="small" icon={<EditOutlined />} onClick={() => setPayableModal({ form: { supplier_id: v.supplier_id, amount: v.amount, due_date: v.due_date, status: v.status, po_id: v.po_id || undefined, pay_date: v.pay_date || '', remark: v.remark || '' }, record: v })}>编辑</Button>
                          <Popconfirm title="删除该应付账款？" onConfirm={() => delPayable(v.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                        </Space>
                      ) },
                    ]}
                    locale={{ emptyText: '暂无应付账款。点「登记应付账款」，记录欠供应商的金额和到期日。' }}
                  />
                </div>
              ),
            }] : []),
          ]}
        />
      </Card>

      {/* 新建采购计划 */}
      <Modal title="新建采购计划（一单多商品）" open={!!poModal} onOk={submitPo} onCancel={() => setPoModal(null)} width={760}>
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={8}><Form.Item name="purchase_date" label="采购时间" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="purchase_month" label="采购月"><DatePicker picker="month" format="YYYY年M月" style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="registrar" label="采购登记人" rules={[{ required: true }]}><Input placeholder="手动填写" /></Form.Item></Col>
            <Col span={12}><Form.Item name="supplier_id" label="供应商"><Select allowClear options={suppliers.map(s => ({ value: s.id, label: s.name }))} placeholder="选择供应商" /></Form.Item></Col>
            <Col span={12}><Form.Item name="warehouse_id" label="存放仓库"><Select allowClear options={warehouses.map(w => ({ value: w.id, label: w.name }))} placeholder="选择仓库" /></Form.Item></Col>
          </Row>
        </Form>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>采购商品明细</div>
        {rows.map((r, i) => (
          <Row gutter={8} key={i} style={{ marginBottom: 8 }}>
            <Col span={12}>
              <Select showSearch placeholder="选择商品编码" value={r.spec_code || undefined} options={productOptions}
                filterOption={(i2, o) => (o.label || '').includes(i2)} style={{ width: '100%' }} onChange={v => updateRow(i, 'spec_code', v)} />
            </Col>
            <Col span={6}><Input placeholder="数量" type="number" value={r.quantity} onChange={e => updateRow(i, 'quantity', e.target.value)} /></Col>
            <Col span={4}><Input placeholder="名称" value={r.product_name} readOnly /></Col>
            <Col span={2}>
              {rows.length > 1 && <Button danger size="small" icon={<DeleteOutlined />} onClick={() => delRow(i)} />}
            </Col>
          </Row>
        ))}
        <Button type="dashed" block icon={<PlusOutlined />} onClick={addRow}>添加一行商品</Button>
      </Modal>

      {/* 入库 */}
      <Modal title={`入库：${inboundModal ? inboundModal.spec : ''}（采购 ${inboundModal ? inboundModal.qty : ''}，已入 ${inboundModal ? inboundModal.done : 0}）`} open={!!inboundModal} onOk={submitInbound} onCancel={() => setInboundModal(null)}>
        <Form form={inboundForm} layout="vertical">
          <Form.Item name="quantity" label="入库数量" rules={[{ required: true }]}>
            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="本次入库数量" />
          </Form.Item>
          <Form.Item name="operator" label="入库操作人" rules={[{ required: true }]}>
            <AutoComplete
              options={operators.map(o => ({ value: o }))}
              placeholder="手动填写或选择历史操作人"
              filterOption={(input, option) => (option?.value || '').includes(input)}
              allowClear
            />
          </Form.Item>
          <Form.Item name="date" label="入库时间" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 调整入库 */}
      <Modal title={`调整入库：${adjustModal ? adjustModal.spec : ''}（修改数量/操作人/时间，保存后自动重算剩余与库存）`} open={!!adjustModal} onOk={submitAdjust} onCancel={() => setAdjustModal(null)} width={700}>
        {(adjRows || []).map((r, i) => (
          <Card key={r.inbound_idx} size="small" title={`第 ${i + 1} 次入库`} style={{ marginBottom: 8 }}>
            <Row gutter={12} align="middle">
              <Col span={8}><span style={{ fontSize: 12, color: '#8c8c8c' }}>数量：</span><InputNumber min={0} precision={0} style={{ width: '100%' }} value={r.qty} onChange={v => setAdjRows(prev => prev.map((x, xi) => xi === i ? { ...x, qty: v } : x))} /></Col>
              <Col span={8}><span style={{ fontSize: 12, color: '#8c8c8c' }}>操作人：</span><Input value={r.operator} onChange={e => setAdjRows(prev => prev.map((x, xi) => xi === i ? { ...x, operator: e.target.value } : x))} /></Col>
              <Col span={8}><span style={{ fontSize: 12, color: '#8c8c8c' }}>时间：</span><DatePicker style={{ width: '100%' }} value={r.date ? dayjs(r.date) : null} onChange={d => setAdjRows(prev => prev.map((x, xi) => xi === i ? { ...x, date: d ? d.format('YYYY-MM-DD') : '' } : x))} /></Col>
            </Row>
          </Card>
        ))}
        <div style={{ color: '#8c8c8c', fontSize: 12 }}>提示：数量改为 0 会移除该条入库记录；保存后已入库/剩余/完成状态与库存自动重新计算。</div>
      </Modal>

      <Modal title={supEdit ? '编辑供应商' : '新增供应商'} open={supModal} onOk={submitSup} onCancel={() => { setSupModal(false); setSupEdit(null); }}>
        <Form form={supForm} layout="vertical">
          <Form.Item name="name" label="供应商名称" rules={[{ required: true, message: '请填供应商名称' }]}><Input /></Form.Item>
          <Form.Item name="tax_no" label="税号（开票方纳税人识别号）"><Input placeholder="选填，用于核对专票" /></Form.Item>
        </Form>
      </Modal>
      <Modal title="新增仓库" open={whModal} onOk={submitWh} onCancel={() => setWhModal(false)}>
        <Form form={whForm} layout="vertical"><Form.Item name="name" label="仓库名称" rules={[{ required: true }]}><Input /></Form.Item></Form>
      </Modal>

      {/* 登记/编辑成本票 */}
      <Modal title={ciModal && ciModal.record ? '编辑成本票' : '登记成本票'} open={!!ciModal} onOk={submitCi} onCancel={() => setCiModal(null)} width={720}>
        <Form form={ciForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}><Form.Item name="supplier_id" label="供应商" rules={[{ required: true, message: '请选择供应商' }]}><Select showSearch optionFilterProp="label" options={ciMeta.suppliers.map(s => ({ value: s.id, label: s.name }))} placeholder="选择供应商" /></Form.Item></Col>
            <Col span={12}><Form.Item name="invoice_type" label="发票类型" rules={[{ required: true }]} initialValue="专票"><Select options={ciMeta.types.map(t => ({ value: t.value, label: t.label }))} /></Form.Item></Col>
            <Col span={8}><Form.Item name="invoice_no" label="发票号码" rules={[{ required: true, message: '请填发票号' }]}><Input placeholder="如 12345678" /></Form.Item></Col>
            <Col span={8}><Form.Item name="invoice_date" label="开票日期" rules={[{ required: true, message: '请选日期' }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="status" label="状态" rules={[{ required: true }]} initialValue="已收到"><Select options={ciMeta.statuses.map(s => ({ value: s, label: s }))} /></Form.Item></Col>
            <Col span={8}><Form.Item name="amount_total" label="价税合计（元）" rules={[{ required: true, message: '请填金额' }]}><InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="含税总金额" /></Form.Item></Col>
            <Col span={8}><Form.Item name="tax" label="税额（元）" tooltip="专票可抵扣的进项税额，普票填 0"><InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="专票填税额" /></Form.Item></Col>
            <Col span={8}><Form.Item name="entity_id" label="归属主体" tooltip="这张票挂在哪个经营主体（公司/个体户）名下"><Select allowClear placeholder="选择主体" options={ciMeta.entities.map(e => ({ value: e.id, label: e.name }))} /></Form.Item></Col>
            <Col span={24}><Form.Item name="remark" label="备注"><Input placeholder="选填" /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* 登记/编辑应付账款 */}
      <Modal title={payableModal && payableModal.record ? '编辑应付账款' : '登记应付账款'} open={!!payableModal}
        onOk={submitPayable} onCancel={() => setPayableModal(null)} width={560}>
        {payableModal && (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {payableModal.record && (
              <div style={{ fontSize: 12, color: '#8C8178', background: '#FFF8F4', border: '1px solid #F3E2D8', borderRadius: 8, padding: '8px 10px', lineHeight: 1.7 }}>
                编号：<b style={{ fontFamily: 'ui-monospace, monospace' }}>{payableModal.record.ap_no || '—'}</b>
                {Number(payableModal.record.paid_amount) > 0 && <span style={{ marginLeft: 12 }}>已核销：<b style={{ color: '#3F8600' }}>¥{yuan(payableModal.record.paid_amount)}</b></span>}
                <span style={{ marginLeft: 12 }}>剩余未付：<b style={{ color: payableModal.record.status === '已付' ? '#3F8600' : '#CF1322' }}>{payableModal.record.status === '已付' ? '已结清' : '¥' + yuan(payableModal.record.remaining)}</b></span>
              </div>
            )}
            <div>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>供应商 <span style={{ color: '#CF1322' }}>*</span></div>
              <Select style={{ width: '100%' }} showSearch optionFilterProp="label" value={payableModal.form.supplier_id}
                onChange={v => setPayableModal(p => ({ ...p, form: { ...p.form, supplier_id: v } }))}
                options={payableMeta.suppliers.map(s => ({ value: s.id, label: (s.code ? s.code + ' ' : '') + s.name }))} placeholder="选择供应商" />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>金额（元）<span style={{ color: '#CF1322' }}>*</span></div>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} value={payableModal.form.amount || null}
                  onChange={v => setPayableModal(p => ({ ...p, form: { ...p.form, amount: v } }))} placeholder="欠款金额" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>到期日</div>
                <DatePicker style={{ width: '100%' }} value={payableModal.form.due_date ? dayjs(payableModal.form.due_date) : null}
                  onChange={d => setPayableModal(p => ({ ...p, form: { ...p.form, due_date: d ? d.format('YYYY-MM-DD') : '' } }))} placeholder="YYYY-MM-DD" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>状态</div>
                <Select style={{ width: '100%' }} value={payableModal.form.status}
                  onChange={v => setPayableModal(p => ({ ...p, form: { ...p.form, status: v } }))}
                  options={[{ value: '未付', label: '未付' }, { value: '已付', label: '已付' }]} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>关联采购单</div>
                <Select style={{ width: '100%' }} allowClear value={payableModal.form.po_id}
                  onChange={v => setPayableModal(p => ({ ...p, form: { ...p.form, po_id: v } }))}
                  options={payableMeta.purchase_orders.map(po => ({ value: po.id, label: '采购单#' + po.id + (po.purchase_date ? '（' + po.purchase_date + '）' : '') }))} placeholder="可选" />
              </div>
            </div>
            {payableModal.form.status === '已付' && (
              <div>
                <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>付款日期</div>
                <DatePicker style={{ width: '100%' }} value={payableModal.form.pay_date ? dayjs(payableModal.form.pay_date) : null}
                  onChange={d => setPayableModal(p => ({ ...p, form: { ...p.form, pay_date: d ? d.format('YYYY-MM-DD') : '' } }))} placeholder="已付请填付款日期" />
              </div>
            )}
            <div>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>备注</div>
              <Input value={payableModal.form.remark} onChange={e => setPayableModal(p => ({ ...p, form: { ...p.form, remark: e.target.value } }))} placeholder="可选" />
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
}
